/**
 * Asset URL Utility
 *
 * Provides centralized asset URL generation using environment-based CDN configuration.
 * All image assets should be referenced through this utility for consistent CDN delivery.
 */

const ASSET_BASE_URL = import.meta.env.VITE_ASSET_BASE_URL || "/assets/images/"

/**
 * Generates the full URL for an asset file from the CDN
 *
 * @param filename - The name of the asset file (e.g., "logo.png", "background.svg")
 * @returns The full URL to the asset on the CDN
 *
 * @example
 * ```tsx
 * // Get logo URL
 * const logoUrl = getAssetUrl("logo-light.png");
 * // Returns: "https://rituality-assets.addvalsolutions.com/images/logo-light.png"
 *
 * // Use in img src
 * <img src={getAssetUrl("background.svg")} alt="Background" />
 *
 * // Use in CSS inline styles
 * <div style={{ backgroundImage: `url(${getAssetUrl("pattern.png")})` }} />
 * ```
 */
export function getAssetUrl(filename: string): string {
  // Remove leading slash if present to avoid double slashes
  const cleanFilename = filename.startsWith("/") ? filename.slice(1) : filename
  // Remove trailing slash from base URL if present to avoid double slashes
  const baseUrl = ASSET_BASE_URL.endsWith("/") ? ASSET_BASE_URL : `${ASSET_BASE_URL}/`
  return `${baseUrl}${cleanFilename}`
}

/**
 * Common asset URLs - pre-configured for frequently used images
 * This provides type-safe access to commonly used assets
 */
export const ASSETS = {
  // Logo assets
  LOGO_DARK: "logo-dark.png",
  LOGO_LIGHT: "logo-light.png",
  SYMBOL: "symbol.svg",

  // Layout backgrounds
  AUTH_BACKGROUND: "auth-bg.jpeg",
  APP_BACKGROUND: "background_image.jpg",
  APP_BACKGROUND_ALT: "app-background.png",

  // Dashboard images
  DASHBOARD_PRIMARY: "dashboard_primary.png",
  BOOK_EXPERT: "book_expert.jpeg",
  CONNECT: "connect.jpeg",
  CREATE: "create.jpeg",
  LEARNING_PRACT: "learning_pract.jpeg",

  // Learn & Practice images
  LEARNING_PRACTICE: "learning_practice.jpeg",
  INTENSION_1: "intension_1.jpg",
  INTENSION_2: "intension_2.jpeg",
  INTENSION_3: "intension_3.jpg",
  INTENSION_4: "intension_4.jpg",

  // Ritual images
  RITUAL_1: "rituals_1.jpg",
  RITUAL_2: "rituals_2.jpeg",
  RITUAL_3: "rituals_3.jpg",
  RITUAL_4: "rituals_4.jpg",
  RITUAL_5: "rituals_5.jpg",
  RITUAL_6: "rituals_6.jpg",
  RITUAL_7: "rituals_7.jpg",

  // Ritual user SVGs
  RITUAL_USER_1: "rituals_user_1.jpg",
  RITUAL_USER_2: "rituals_user_2.jpg",
  RITUAL_USER_3: "rituals_user_3.jpg",
  RITUAL_USER_4: "rituals_user_4.jpg",

  // Trusted rituals
  TRUSTED_RITUAL_1: "trusted_ritual_1.jpeg",
  TRUSTED_RITUAL_2: "trusted_ritual_2.jpg",
  TRUSTED_RITUAL_3: "trusted_ritual_3.jpg",
  TRUSTED_RITUAL_4: "trusted_ritual_4.jpg",
  TRUSTED_RITUAL_5: "trusted_ritual_5.jpg",

  // Profile
  UPLOAD_BG: "upload-bg.jpeg",
} as const

/**
 * Type-safe asset URL getter using the ASSETS constant
 *
 * @param assetKey - Key from the ASSETS constant
 * @returns The full URL to the asset on the CDN
 *
 * @example
 * ```tsx
 * import { getAsset, ASSETS } from "@/utils/assets";
 *
 * <img src={getAsset(ASSETS.LOGO_LIGHT)} alt="Logo" />
 * ```
 */
export function getAsset(assetKey: keyof typeof ASSETS): string {
  return getAssetUrl(ASSETS[assetKey])
}

/**
 * Fallback placeholder image URL
 */
export const PLACEHOLDER_IMAGE = getAssetUrl("placeholder.png")
